import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { trackFeatureUsage, trackFormSubmission, trackEvent } from '../utils/analytics';

const MealPlanTemplates = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({
    templateName: '',
    weightCategory: '',
    foodPreference: 'VEG',
    instructions: '',
    items: [
      { timeSlot: 'On waking up', description: '', notes: '', sortOrder: 1 },
      { timeSlot: '9:00 AM', description: '', notes: '', sortOrder: 2 },
      { timeSlot: '9:00 am to 1:00 pm', description: '', notes: '', sortOrder: 3 },
      { timeSlot: '1:00 PM', description: '', notes: '', sortOrder: 4 },
      { timeSlot: '5:00 PM', description: '', notes: '', sortOrder: 5 },
      { timeSlot: '8:00 PM', description: '', notes: '', sortOrder: 6 }
    ]
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');

  useEffect(() => {
    fetchTemplates();
    trackFeatureUsage('Meal Plan Templates', 'view');
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/meal-plan-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch meal plan templates:', error);
      setError('Failed to load meal plan templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      await axios.post('/api/meal-plan-templates', newTemplate);
      
      trackFormSubmission('add_meal_plan_template_form', true);
      trackEvent('meal_plan_template_added', {
        event_category: 'Meal Plan Management',
        event_label: newTemplate.templateName,
        weight_category: newTemplate.weightCategory,
        food_preference: newTemplate.foodPreference
      });
      
      setShowAddForm(false);
      setConfirmMessage('Meal plan template added successfully!');
      setShowConfirmModal(true);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Failed to add meal plan template:', error);
      trackFormSubmission('add_meal_plan_template_form', false);
      setFormError(error.response?.data?.message || 'Failed to add meal plan template. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditTemplate = async (template) => {
    try {
      const response = await axios.get(`/api/meal-plan-templates/${template.id}`);
      setEditingTemplate(response.data);
      setNewTemplate({
        templateName: response.data.templateName,
        weightCategory: response.data.weightCategory,
        foodPreference: response.data.foodPreference,
        instructions: response.data.instructions || '',
        items: response.data.items || []
      });
      setShowEditForm(true);
    } catch (error) {
      console.error('Failed to fetch template details:', error);
      setError('Failed to load template details. Please try again.');
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      await axios.put(`/api/meal-plan-templates/${editingTemplate.id}`, newTemplate);
      
      trackFormSubmission('edit_meal_plan_template_form', true);
      trackEvent('meal_plan_template_updated', {
        event_category: 'Meal Plan Management',
        event_label: newTemplate.templateName,
        template_id: editingTemplate.id
      });
      
      setShowEditForm(false);
      setEditingTemplate(null);
      setConfirmMessage('Meal plan template updated successfully!');
      setShowConfirmModal(true);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Failed to update meal plan template:', error);
      trackFormSubmission('edit_meal_plan_template_form', false);
      setFormError(error.response?.data?.message || 'Failed to update meal plan template. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this meal plan template?')) {
      return;
    }

    try {
      await axios.delete(`/api/meal-plan-templates/${templateId}`);
      setConfirmMessage('Meal plan template deleted successfully!');
      setShowConfirmModal(true);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete meal plan template:', error);
      setError('Failed to delete meal plan template. Please try again.');
    }
  };

  const resetForm = () => {
    setNewTemplate({
      templateName: '',
      weightCategory: '',
      foodPreference: 'VEG',
      instructions: '',
      items: [
        { timeSlot: 'On waking up', description: '', notes: '', sortOrder: 1 },
        { timeSlot: '9:00 AM', description: '', notes: '', sortOrder: 2 },
        { timeSlot: '9:00 am to 1:00 pm', description: '', notes: '', sortOrder: 3 },
        { timeSlot: '1:00 PM', description: '', notes: '', sortOrder: 4 },
        { timeSlot: '5:00 PM', description: '', notes: '', sortOrder: 5 },
        { timeSlot: '8:00 PM', description: '', notes: '', sortOrder: 6 }
      ]
    });
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...newTemplate.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setNewTemplate({ ...newTemplate, items: updatedItems });
  };

  const addNewItem = () => {
    const newItem = {
      timeSlot: '',
      description: '',
      notes: '',
      sortOrder: newTemplate.items.length + 1
    };
    setNewTemplate({
      ...newTemplate,
      items: [...newTemplate.items, newItem]
    });
  };

  const removeItem = (index) => {
    const updatedItems = newTemplate.items.filter((_, i) => i !== index);
    // Update sort orders
    updatedItems.forEach((item, i) => {
      item.sortOrder = i + 1;
    });
    setNewTemplate({ ...newTemplate, items: updatedItems });
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading meal plan templates...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <h1 className="page-title" style={{ margin: 0 }}>Meal Plan Templates</h1>
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--gray-dark)', 
            marginTop: '5px'
          }}>
            Manage meal plan templates for different weight categories and food preferences
          </div>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowAddForm(true);
          }}
          style={{ flexShrink: 0 }}
        >
          <span className="btn-icon">➕</span>
          <span className="btn-text">Add New Template</span>
        </button>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            Meal Plan Templates ({templates.length} total)
          </h3>
        </div>

        {templates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            No meal plan templates found. Click "Add New Template" to create your first template.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Template Name
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Weight Category
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Food Preference
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Created Date
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'center',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template, index) => (
                  <tr 
                    key={template.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {template.templateName}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {template.weightCategory}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        background: template.foodPreference === 'VEG' ? 'var(--success)' : 
                                   template.foodPreference === 'NON_VEG' ? 'var(--error)' : 
                                   template.foodPreference === 'VEGAN' ? 'var(--primary-blue)' : 'var(--warning)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {template.foodPreference}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {new Date(template.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => handleEditTemplate(template)}
                          title="Edit Template"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                          onClick={() => handleDeleteTemplate(template.id)}
                          title="Delete Template"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Template Modal */}
      {(showAddForm || showEditForm) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '95vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {showEditForm ? 'Edit Meal Plan Template' : 'Add New Meal Plan Template'}
            </h3>

            {formError && (
              <div className="alert alert-error">{formError}</div>
            )}

            <form onSubmit={showEditForm ? handleUpdateTemplate : handleAddTemplate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label htmlFor="templateName" className="form-label">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    id="templateName"
                    className="form-input"
                    value={newTemplate.templateName}
                    onChange={(e) => setNewTemplate({...newTemplate, templateName: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="e.g., Weight Loss Plan"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="weightCategory" className="form-label">
                    Weight Category *
                  </label>
                  <input
                    type="text"
                    id="weightCategory"
                    className="form-input"
                    value={newTemplate.weightCategory}
                    onChange={(e) => setNewTemplate({...newTemplate, weightCategory: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="e.g., Below 70kg"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="foodPreference" className="form-label">
                    Food Preference *
                  </label>
                  <select
                    id="foodPreference"
                    className="form-input"
                    value={newTemplate.foodPreference}
                    onChange={(e) => setNewTemplate({...newTemplate, foodPreference: e.target.value})}
                    required
                    disabled={formLoading}
                  >
                    <option value="VEG">Vegetarian</option>
                    <option value="NON_VEG">Non-Vegetarian</option>
                    <option value="VEGAN">Vegan</option>
                    <option value="JAIN">Jain</option>
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="instructions" className="form-label">
                  Instructions
                </label>
                <textarea
                  id="instructions"
                  className="form-input"
                  rows="4"
                  value={newTemplate.instructions || ''}
                  onChange={(e) => setNewTemplate({...newTemplate, instructions: e.target.value})}
                  disabled={formLoading}
                  placeholder="Optional instructions for this template..."
                />
              </div>

              {/* Meal Plan Items */}
              <div style={{
                border: '2px solid var(--primary-blue)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ color: 'var(--dark-blue)', margin: 0 }}>Meal Plan Schedule</h4>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={addNewItem}
                    disabled={formLoading}
                  >
                    Add Time Slot
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {newTemplate.items.map((item, index) => (
                    <div key={index} style={{
                      border: '1px solid var(--gray-medium)',
                      borderRadius: '8px',
                      padding: '15px',
                      background: 'var(--gray-light)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h5 style={{ color: 'var(--primary-blue)', margin: 0 }}>
                          Time Slot {index + 1}
                        </h5>
                        {newTemplate.items.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                            onClick={() => removeItem(index)}
                            disabled={formLoading}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr 1fr', gap: '15px' }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Time *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={item.timeSlot}
                            onChange={(e) => updateItem(index, 'timeSlot', e.target.value)}
                            required
                            disabled={formLoading}
                            placeholder="e.g., 9:00 AM"
                          />
                        </div>

                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Description *</label>
                          <textarea
                            className="form-input"
                            rows="3"
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            required
                            disabled={formLoading}
                            placeholder="Describe the meal or activity..."
                          />
                        </div>

                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Notes</label>
                          <textarea
                            className="form-input"
                            rows="3"
                            value={item.notes}
                            onChange={(e) => updateItem(index, 'notes', e.target.value)}
                            disabled={formLoading}
                            placeholder="Additional notes or instructions..."
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setShowEditForm(false);
                    setEditingTemplate(null);
                    setFormError('');
                    resetForm();
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formLoading}
                >
                  {formLoading ? (showEditForm ? 'Updating...' : 'Adding...') : (showEditForm ? 'Update Template' : 'Add Template')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
              Success!
            </h3>
            <p style={{ color: 'var(--gray-dark)', marginBottom: '25px' }}>
              {confirmMessage}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowConfirmModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealPlanTemplates;
